import { useSyncExternalStore } from "react";

type Token = symbol;
type Entry = { token: Token; alert: boolean };

let stack: Entry[] = [];
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

export const pushModal = (token: Token, alert: boolean) => {
  stack = [...stack, { token, alert }];
  emit();
};

export const popModal = (token: Token) => {
  stack = stack.filter((e) => e.token !== token);
  emit();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Stacked modals peek out behind the top one. The visible gap between adjacent
// cards shrinks with depth (inverse-logarithmic) so the top few are clearly
// separated while an arbitrarily deep stack stays compact instead of marching
// off-screen — which is what lets us drop a hard stack limit entirely.
const STACK_OFFSET_PX = 20;
const STACK_SCALE_STEP = 0.07;

export function stackTransform(depth: number) {
  const t = Math.log1p(depth);
  return { offset: STACK_OFFSET_PX * t, scale: 1 - STACK_SCALE_STEP * t };
}

export function modalPosition(entries: Entry[], token: Token) {
  const index = entries.findIndex((e) => e.token === token);
  if (index === -1)
    return {
      depth: 0,
      isTop: true,
      isBottom: entries.length === 0,
      coveredByAlert: false,
    };
  return {
    depth: entries.length - 1 - index,
    isTop: index === entries.length - 1,
    isBottom: index === 0,
    coveredByAlert: entries[index + 1]?.alert ?? false,
  };
}

export function useModalPosition(token: Token) {
  const entries = useSyncExternalStore(subscribe, () => stack);
  return modalPosition(entries, token);
}
